import { useTenantConfig } from 'contexts';
import {
  getDiscountByCombo,
  getNewComboData,
  getTotalAmount,
  getTotalDiscount,
  mapComboWithKey,
} from 'helpers';
import { useGetProductsCombos } from 'hooks/useDetailPromotion';
import { t } from 'i18n';
import { useEffect, useMemo, useState } from 'react';
import {
  ComboItem,
  IActiveCombo,
  IComboGroup,
  IComboWithKey,
  ISelectingProduct,
  ProductComboScreen,
} from 'types';
import {
  useRecommendationCombosData,
  useRecommendationProductsData,
} from './useDiscovery';

export const useComboList = (sku?: string) => {
  const {
    result: { combos } = { combos: [] },
    isLoading: isLoadingDynamicCombo,
  } = useRecommendationCombosData(sku);
  const {
    result: { productSets } = { productSets: [] },
    isLoading: isLoadingAutogenCombo,
  } = useRecommendationProductsData(sku);
  const getCookedComboList = (): ComboItem[] => {
    const allCombos: ComboItem[] = [];
    for (let i = 0; i < combos.length; i++) {
      const combo = combos[i];
      const mainProductIndex = combo.products.findIndex(
        (el) => el.product.productInfo.sku === sku,
      );
      const mainProduct = combo.products[mainProductIndex];
      const restProducts = combo.products.filter(
        (_el, index) => index !== mainProductIndex,
      );
      allCombos.push({
        type: 'dynamic',
        items: [mainProduct, ...restProducts].map((item) => ({
          quantity: item.quantity,
          product: item.product,
        })),
        promotionId: combo.id,
        promotion: combo.promotion,
        supplierRetailPrice: Number(combo.supplierRetailPrice),
        latestPrice: Number(combo.latestPrice),
      });
    }
    for (let i = 0; i < productSets.length; i++) {
      const productSet = productSets[i];
      const supplierRetailPrice = productSet.products.reduce(
        (acc, curr) =>
          acc + Number(curr?.prices?.[0]?.supplierRetailPrice || 0),
        0,
      );
      const latestPrice = productSet.products.reduce(
        (acc, curr) => acc + Number(curr?.prices?.[0]?.latestPrice || 0),
        0,
      );
      const mainProduct = productSet.products.find(
        (el) => el.productInfo.sku === sku,
      );

      const restProducts = productSet.products.filter(
        (el) => el.productInfo.sku !== sku,
      );
      mainProduct &&
        allCombos.push({
          type: 'autogen',
          items: [mainProduct, ...restProducts].map((prod) => ({
            quantity: 1,
            product: prod,
          })),
          supplierRetailPrice,
          latestPrice,
        });
    }
    return allCombos;
  };
  return {
    isLoading: isLoadingDynamicCombo || isLoadingAutogenCombo,
    comboList: getCookedComboList(),
  };
};
const TITLE_MAP = {
  autogen: t('Thường được mua cùng'),
  dynamic: t('Mua kèm nhận ưu đãi'),
};
export const useComboTitle = (comboList: ComboItem[]) => {
  // handle title 'Mua kèm nhận ưu đãi' || 'Thường được mua cùng'
  const [title, setTitle] = useState(() => {
    if (comboList.length === 0) return undefined;
    return TITLE_MAP[comboList[0].type];
  });
  const onSlideChange = (index: number) => {
    setTitle(TITLE_MAP[comboList[index].type]);
  };
  useEffect(() => {
    if (comboList.length > 0) setTitle(TITLE_MAP[comboList[0].type]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(comboList)]);
  return {
    title,
    onSlideChange,
  };
};

type ProductComboProps = {
  screen?: ProductComboScreen;
  skus?: string[];
  selectingSku?: string;
  hasCompulsoryProducts?: boolean;
};

export const useProductCombos = ({
  screen = ProductComboScreen.product_detail,
  skus,
  selectingSku,
  hasCompulsoryProducts = false,
}: ProductComboProps) => {
  const { applyNewLogicToCalculateComboPrice = false } = useTenantConfig();
  const { result, isLoading: isLoadingCombos } = useGetProductsCombos(
    skus,
    hasCompulsoryProducts,
  );
  // Add key for each combo & condition in combo (to serve calculating/updating on FE)
  const productCombos: IComboWithKey[] = useMemo(() => {
    const selectedProductCombo = result.products.filter(
      (pd) => pd.sku === selectingSku,
    );
    return !isLoadingCombos && result.products.length > 0
      ? screen === ProductComboScreen.product_detail
        ? mapComboWithKey(result.products)[0]
        : // only map combo with key for combo that has sku match current changing/add combo item in cart && current screen is cart
        selectedProductCombo.length > 0
        ? mapComboWithKey(selectedProductCombo)[0]
        : []
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, isLoadingCombos, selectingSku]);

  // Group of combos that have the same number of products
  const [comboGroup, setComboGroup] = useState<IComboGroup>();

  const [activeCombo, setActiveCombo] = useState<IActiveCombo>();

  const selectComboProduct = (
    selectingProduct: ISelectingProduct,
    setSelectingCombo?: (combo: IActiveCombo) => void,
  ) => {
    if (comboGroup && activeCombo) {
      const { newActiveCombo, newGroup } = getNewComboData(
        selectingProduct,
        comboGroup,
        activeCombo,
        applyNewLogicToCalculateComboPrice,
      );

      if (!setSelectingCombo) {
        setActiveCombo(newActiveCombo);
        setComboGroup(newGroup);
      } else {
        setSelectingCombo(newActiveCombo);
      }
    } else {
      throw Error('Invalid comboGroup or activeCombo');
    }
  };

  useEffect(() => {
    if (productCombos.length) {
      // Group combos with the same number of products
      const groupObj = productCombos.reduce<IComboGroup>((result, cb) => {
        const combos = result[cb.totalProduct]?.combos || [];
        const comboTemp = {
          ...cb,
          // in the first render -> select the first product in each condition to show
          conditions: cb.conditions.map(({ products, ...restCondition }) => ({
            ...restCondition,
            product: products[0],
          })),
        };
        combos.push({
          ...comboTemp,
          discountByCombo: getDiscountByCombo(
            comboTemp,
            applyNewLogicToCalculateComboPrice,
          ),
          totalDiscount: getTotalDiscount(
            comboTemp,
            applyNewLogicToCalculateComboPrice,
          ),
          totalAmount: getTotalAmount(
            comboTemp,
            applyNewLogicToCalculateComboPrice,
          ),
        });
        return {
          ...result,
          [cb.totalProduct]: { combos },
        };
      }, {});

      setComboGroup(groupObj);
      if (!activeCombo) {
        // Default set the first combo in the first group is active
        const firstGroup: { combos: IActiveCombo[] } =
          Object.values(groupObj)[0];
        setActiveCombo(firstGroup.combos[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(productCombos)]);

  return {
    productCombosData: result.products,
    comboGroup,
    setComboGroup,
    productCombos,
    activeCombo,
    setActiveCombo,
    isLoadingCombos,
    selectComboProduct,
  };
};
